import { AlertTriangle } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const TaskManagerContent = ({ scenario, onComplete, processes = [], onEndTask, systemSpecs, networkFlows = [] }) => {
  const [activeTab, setActiveTab] = useState('processes');
  const [sortConfig, setSortConfig] = useState({ column: 'cpu', direction: 'desc' });
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [performanceData, setPerformanceData] = useState({
    cpu: Array(60).fill(0),
    memory: Array(60).fill(0),
    disk: Array(60).fill(0),
    network: Array(60).fill(0),
  });

  // Local state: computed current values for CPU/Memory
  const [processMetrics, setProcessMetrics] = useState({});

  // System resource limits (with defaults if not provided)
  const totalMemoryMB = systemSpecs?.totalMemoryMB || 32768;
  const totalCPUCores = systemSpecs?.totalCPUCores || 12;
  const maxDiskIOmbps = systemSpecs?.maxDiskIOmbps || 500;
  const maxNetworkMbps = systemSpecs?.maxNetworkMbps || 1000;

  // Combined metric update: compute per-process and system totals together (always in sync)
  useEffect(() => {
    const interval = setInterval(() => {
      const newMetrics = {};
      let totalCPU = 0;
      let totalMemory = 0;
      let totalDisk = 0;
      let totalNetwork = 0;

      // Compute current values for all processes
      processes.forEach(process => {
        // Calculate: mean ± random jitter
        const cpuCurrent = Math.max(0, process.cpu.mean + (Math.random() - 0.5) * 2 * process.cpu.jitter);
        const memoryCurrent = Math.max(0, process.memory.mean + (Math.random() - 0.5) * 2 * process.memory.jitter);
        const diskCurrent = Math.max(0, process.disk.mean + (Math.random() - 0.5) * 2 * process.disk.jitter);
        const networkCurrent = Math.max(0, process.network.mean + (Math.random() - 0.5) * 2 * process.network.jitter);

        const metrics = {
          cpu: parseFloat(cpuCurrent.toFixed(2)),
          memory: parseFloat(memoryCurrent.toFixed(1)),
          disk: parseFloat(diskCurrent.toFixed(1)),
          network: parseFloat(networkCurrent.toFixed(1))
        };

        newMetrics[process.id] = metrics;

        // Accumulate totals immediately
        totalCPU += metrics.cpu;
        totalMemory += metrics.memory;
        totalDisk += metrics.disk;
        totalNetwork += metrics.network;
      });

      // Update per-process metrics
      setProcessMetrics(newMetrics);

      // Calculate system-wide values
      // CPU: Process percentages are already expressed as % of total system capacity
      const cpuPercentage = Math.min(100, totalCPU);

      // Memory: Convert MB usage to percentage of total memory
      const memoryPercentage = Math.min(100, (totalMemory / totalMemoryMB) * 100);

      // Disk: Total MB/s usage (will be capped at graph level)
      const diskUsage = totalDisk;

      // Network: Total Mbps usage (clamp to max)
      const networkUsage = Math.min(maxNetworkMbps, totalNetwork);

      // Update performance graphs
      setPerformanceData(prev => ({
        cpu: [...prev.cpu.slice(1), cpuPercentage],
        memory: [...prev.memory.slice(1), memoryPercentage],
        disk: [...prev.disk.slice(1), diskUsage],
        network: [...prev.network.slice(1), networkUsage],
      }));
    }, 1000); // Update every 1 second

    return () => clearInterval(interval);
  }, [processes, totalMemoryMB, maxDiskIOmbps, maxNetworkMbps]);

  // Use OS-provided processes (always trust the OS process manager)
  const processList = processes;

  const sortProcesses = (processes) => {
    return [...processes].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.column) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'cpu':
          // Use computed current value or fall back to mean
          aVal = processMetrics[a.id]?.cpu ?? a.cpu?.mean ?? 0;
          bVal = processMetrics[b.id]?.cpu ?? b.cpu?.mean ?? 0;
          break;
        case 'memory':
          // Use computed current value or fall back to mean
          aVal = processMetrics[a.id]?.memory ?? a.memory?.mean ?? 0;
          bVal = processMetrics[b.id]?.memory ?? b.memory?.mean ?? 0;
          break;
        case 'disk':
          // Use computed current value or fall back to mean
          aVal = processMetrics[a.id]?.disk ?? a.disk?.mean ?? 0;
          bVal = processMetrics[b.id]?.disk ?? b.disk?.mean ?? 0;
          break;
        case 'network':
          // Use computed current value or fall back to mean
          aVal = processMetrics[a.id]?.network ?? a.network?.mean ?? 0;
          bVal = processMetrics[b.id]?.network ?? b.network?.mean ?? 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleEndTask = () => {
    if (selectedProcess && onEndTask) {
      onEndTask(selectedProcess.id);
      setSelectedProcess(null);
    }
  };

  const sortedProcesses = sortProcesses(processList);

  const PerformanceGraph = ({ data, color, label, unit = '%', subtitle, fixedMax = null }) => {
    // Use fixed max if provided (for percentage graphs), otherwise auto-scale
    const max = fixedMax !== null ? fixedMax : Math.max(...data, 1);
    const current = data[data.length - 1] || 0;

    return (
      <div className="flex flex-col">
        <div className="mb-2">
          <div className="text-lg font-semibold">{label}</div>
          <div className="text-3xl font-mono">{current.toFixed(1)}{unit}</div>
          {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
        </div>
        <div className="relative h-32 bg-gray-800 rounded border border-gray-700">
          <svg className="w-full h-full" viewBox="0 0 300 100" preserveAspectRatio="none">
            <polyline
              fill="none"
              stroke={color}
              strokeWidth="2"
              points={data.map((val, i) => `${(i / 59) * 300},${100 - (val / max) * 100}`).join(' ')}
            />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-gray-900 text-white flex flex-col">
      {/* Tabs */}
      <div className="flex border-b border-gray-700">
        <button
          onClick={() => setActiveTab('processes')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'processes'
              ? 'bg-gray-800 border-b-2 border-blue-500'
              : 'hover:bg-gray-800'
            }`}
        >
          Processes
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'performance'
              ? 'bg-gray-800 border-b-2 border-blue-500'
              : 'hover:bg-gray-800'
            }`}
        >
          Performance
        </button>
        <button
          onClick={() => setActiveTab('network')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'network'
              ? 'bg-gray-800 border-b-2 border-blue-500'
              : 'hover:bg-gray-800'
            }`}
        >
          Network
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'processes' ? (
          <div className="h-full flex flex-col">
            {/* Processes Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full">
                <thead className="bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th
                      onClick={() => handleSort('name')}
                      className="text-left p-3 text-sm font-medium cursor-pointer hover:bg-gray-700 select-none"
                    >
                      Name {sortConfig.column === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('cpu')}
                      className="text-right p-3 text-sm font-medium cursor-pointer hover:bg-gray-700 select-none"
                    >
                      CPU {sortConfig.column === 'cpu' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('memory')}
                      className="text-right p-3 text-sm font-medium cursor-pointer hover:bg-gray-700 select-none"
                    >
                      Memory {sortConfig.column === 'memory' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('disk')}
                      className="text-right p-3 text-sm font-medium cursor-pointer hover:bg-gray-700 select-none"
                    >
                      Disk {sortConfig.column === 'disk' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      onClick={() => handleSort('network')}
                      className="text-right p-3 text-sm font-medium cursor-pointer hover:bg-gray-700 select-none"
                    >
                      Network {sortConfig.column === 'network' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProcesses.map((process) => (
                    <tr
                      key={process.id}
                      onClick={() => setSelectedProcess(process)}
                      className={`border-b border-gray-800 cursor-pointer transition-colors ${selectedProcess?.id === process.id
                          ? 'bg-blue-900'
                          : process.suspicious || process.isRogue
                            ? 'bg-red-900/30 hover:bg-red-900/50'
                            : 'hover:bg-gray-800'
                        }`}
                    >
                      <td className="p-3 text-sm">
                        <div className="flex items-center">
                          {(process.suspicious || process.isRogue) && (
                            <AlertTriangle size={14} className="mr-2 text-red-500 flex-shrink-0" />
                          )}
                          <span className={process.suspicious || process.isRogue ? 'text-red-400 font-medium' : ''}>
                            {process.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-right font-mono">
                        {(processMetrics[process.id]?.cpu ?? process.cpu?.mean ?? 0).toFixed(1)}%
                      </td>
                      <td className="p-3 text-sm text-right font-mono">
                        {(processMetrics[process.id]?.memory ?? process.memory?.mean ?? 0).toFixed(1)} MB
                      </td>
                      <td className="p-3 text-sm text-right font-mono">
                        {(processMetrics[process.id]?.disk ?? process.disk?.mean ?? 0).toFixed(1)} MB/s
                      </td>
                      <td className="p-3 text-sm text-right font-mono">
                        {(processMetrics[process.id]?.network ?? process.network?.mean ?? 0).toFixed(1)} Mbps
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* End Task Button */}
            <div className="p-3 border-t border-gray-700 bg-gray-800">
              <button
                onClick={handleEndTask}
                disabled={!selectedProcess}
                className={`px-4 py-2 rounded transition-colors ${selectedProcess
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
              >
                End Task
              </button>
            </div>
          </div>
        ) : activeTab === 'performance' ? (
          <div className="p-6 grid grid-cols-2 gap-6 overflow-auto">
            <PerformanceGraph
              data={performanceData.cpu}
              color="#3b82f6"
              label="CPU"
              unit="%"
              subtitle={`${totalCPUCores} Cores, ${systemSpecs?.processor || 'Unknown Processor'}`}
              fixedMax={100}
            />
            <PerformanceGraph
              data={performanceData.memory}
              color="#a855f7"
              label="Memory"
              unit="%"
              subtitle={`${((performanceData.memory[performanceData.memory.length - 1] || 0) / 100 * totalMemoryMB / 1024).toFixed(1)} / ${(totalMemoryMB / 1024).toFixed(1)} GB (${((performanceData.memory[performanceData.memory.length - 1] || 0) / 100 * totalMemoryMB).toFixed(0)} MB)`}
              fixedMax={100}
            />
            <PerformanceGraph
              data={performanceData.disk}
              color="#22c55e"
              label="Disk"
              unit=" MB/s"
              subtitle={`Max: ${maxDiskIOmbps} MB/s`}
              fixedMax={maxDiskIOmbps}
            />
            <PerformanceGraph
              data={performanceData.network}
              color="#f97316"
              label="Network"
              unit=" Mbps"
              subtitle={`Max: ${maxNetworkMbps} Mbps`}
            />
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Network Flows Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full">
                <thead className="bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">Source</th>
                    <th className="text-left p-3 text-sm font-medium">Destination</th>
                    <th className="text-left p-3 text-sm font-medium">Protocol</th>
                    <th className="text-right p-3 text-sm font-medium">Bytes</th>
                    <th className="text-left p-3 text-sm font-medium">Note</th>
                    <th className="text-left p-3 text-sm font-medium">First Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {networkFlows.map((flow) => (
                    <tr
                      key={flow.id}
                      className={`border-b border-gray-800 ${flow.isSuspicious
                          ? 'bg-red-900/30 hover:bg-red-900/50'
                          : 'hover:bg-gray-800'
                        }`}
                    >
                      <td className="p-3 text-sm font-mono">
                        {flow.isSuspicious && (
                          <AlertTriangle size={14} className="inline mr-2 text-red-500" />
                        )}
                        {flow.src}
                      </td>
                      <td className="p-3 text-sm font-mono font-medium text-red-400">
                        {flow.dst}
                      </td>
                      <td className="p-3 text-sm">{flow.protocol}</td>
                      <td className="p-3 text-sm text-right font-mono">
                        {(flow.bytes / 1024).toFixed(2)} KB
                      </td>
                      <td className="p-3 text-sm text-yellow-400">{flow.note || '-'}</td>
                      <td className="p-3 text-sm text-gray-400">
                        {flow.firstSeen ? new Date(flow.firstSeen).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                  {networkFlows.length === 0 && (
                    <tr>
                      <td colSpan="6" className="p-6 text-center text-gray-400">
                        No network flows detected
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskManagerContent;